import { Flex } from "src/components/Flex.tsx";
import React from "react";

import { useFormContext } from "react-hook-form";
import { FormField } from "src/components/Form/fields/FormField.tsx";
import { ChipV2 } from "src/components/ChipV2/ChipV2.tsx";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useIsFieldDisabled } from "src/components/Form/hooks/useIsFieldDisabled.ts";
import { useIsFieldHidden } from "src/components/Form/hooks/useIsFieldHidden.ts";
import type { InputRangeField } from "src/components/Form/Form.types.ts";

export const RangeField = ({ field }: { field: InputRangeField }) => {
  const form = useFormContext();

  const { nameFrom, additionalField, shortcuts = [], nameTo, label } = field;

  const { isDisabled: nameFromDisabled } = useIsFieldDisabled({
    name: nameFrom,
  });
  const { isDisabled: nameToDisabled } = useIsFieldDisabled({
    name: nameTo,
  });

  const { isHidden: nameFromHidden } = useIsFieldHidden({
    name: nameFrom,
  });
  const { isHidden: nameToHidden } = useIsFieldHidden({
    name: nameTo,
  });

  const hidden = nameFromHidden && nameToHidden;

  const disabled = nameFromDisabled && nameToDisabled;

  if (hidden) {
    return null;
  }

  return (
    <div css={container}>
      <label css={labelStyles}>{label}</label>
      <div css={inputsContainer}>
        <div css={inputContainer}>
          <FormField name={nameFrom} />
        </div>
        <div css={minus}>-</div>
        <div css={inputContainer}>
          <FormField name={nameTo} />
        </div>
        {additionalField && (
          <div css={radio}>
            <FormField name={additionalField} />
          </div>
        )}
      </div>
      {shortcuts.length > 0 && (
        <Flex
          gap={"10px"}
          style={{
            marginTop: "10px",
          }}
        >
          {shortcuts.map((shortcut) => (
            <ChipV2
              key={shortcut.id}
              label={shortcut.label}
              skin={"grey"}
              disabled={disabled}
              onClick={() => {
                if (disabled) {
                  return;
                }
                const value = shortcut.getValue();
                form.setValue(nameFrom, value[0]);
                form.setValue(nameTo, value[1]);
              }}
            />
          ))}
        </Flex>
      )}
    </div>
  );
};

const container = css`
  display: flex;
  flex-direction: column;
`;

const minus = css`
  display: flex;
  align-items: center;
  height: 37px;
`;

const inputsContainer = css`
  display: flex;
  gap: 10px;
`;

const radio = css`
  height: 37px;
  align-items: center;
  display: flex;
`;

const labelStyles = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.body};
  font-weight: ${theme.fontWeights.normal};
  color: ${theme.colorsV2.grey[800]};
  line-height: 21px;
`;

const inputContainer = css`
  width: 110px;
`;
